import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';

const CalendarGrid = ({
    holidays,
    calendarRows,      
    weekendsVisible,   
    events,            
    expandedDates: parentExpandedDates, 
    user,              
    handleDateClick,   
    handleEventClick,  
    handleToggleExpand,
    isCalFull 
}) => {
    const activeCurrentMonth = (() => {
        if (calendarRows && calendarRows.length > 2) {
            const middleWeek = calendarRows[2];
            const middleDay = middleWeek[0]?.date || middleWeek[0];
            return dayjs(middleDay).month();
        }
        return dayjs().month();
    })();

    const [localExpandedDates, setLocalExpandedDates] = useState([]);
    const expandedDates = parentExpandedDates && parentExpandedDates.length > 0 ? parentExpandedDates : localExpandedDates;
    const maxVisibleCount = 3; 

    // 더보기 / 접기 토글 핸들러
    const toggleExpand = (dateStr, e) => {
        e.stopPropagation();
        setLocalExpandedDates(prev =>
            prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]
        );
        if (handleToggleExpand) handleToggleExpand(dateStr, e);
    };

    return (
        <Box sx={{ border: '1px solid #e0e0e0', backgroundColor: '#e0e0e0', display: 'flex', flexDirection: 'column', gap: '1px', width: '100%' }}>
            {calendarRows.map((week, weekIdx) => {
                const weekStart = dayjs(week[0]?.date || week[0]).startOf('day');
                const weekEnd = dayjs(week[week.length - 1]?.date || week[week.length - 1]).endOf('day');
                
                const matchedEvents = (events || []).filter(event => {
                    if (!event.start || !event.end) return false;
                    const eStart = dayjs(event.start.substring(0, 10)).startOf('day');
                    const eEnd = dayjs(event.end.substring(0, 10)).endOf('day');
                    return (eStart.isBefore(weekEnd) || eStart.isSame(weekEnd, 'day')) && 
                           (eEnd.isAfter(weekStart) || eEnd.isSame(weekStart, 'day'));
                }).sort((a, b) => dayjs(a.start).diff(dayjs(b.start)));

                const timelineTracks = [];
                const renderedWeekEvents = [];
                
                matchedEvents.forEach(event => {
                    const eStart = dayjs(event.start.substring(0, 10)).startOf('day');
                    const eEnd = dayjs(event.end.substring(0, 10)).endOf('day');
                    const startIdx = Math.max(0, eStart.isBefore(weekStart) ? 0 : eStart.day());
                    const endIdx = Math.min(week.length - 1, eEnd.isAfter(weekEnd) ? week.length - 1 : eEnd.day());

                    let adjustedStart = startIdx;
                    let adjustedEnd = endIdx;
                    if (!weekendsVisible) {
                        if (startIdx === 0) adjustedStart = 1; 
                        if (endIdx === 6) adjustedEnd = 5;     
                        adjustedStart -= 1; adjustedEnd -= 1;
                        if (adjustedStart > 4 || adjustedEnd < 0) return; 
                    }
                    const colSpan = adjustedEnd - adjustedStart + 1;

                    let targetTrackIndex = -1;
                    for (let i = 0; i < timelineTracks.length; i++) {
                        const hasOverlap = timelineTracks[i].some(allocated => !(adjustedEnd < allocated.startIdx || adjustedStart > allocated.endIdx));
                        if (!hasOverlap) { targetTrackIndex = i; break; }
                    }
                    if (targetTrackIndex === -1) {
                        timelineTracks.push([{ startIdx: adjustedStart, endIdx: adjustedEnd }]);
                        targetTrackIndex = timelineTracks.length - 1;
                    } else {
                        timelineTracks[targetTrackIndex].push({ startIdx: adjustedStart, endIdx: adjustedEnd });
                    }
                    renderedWeekEvents.push({ ...event, startIdx: adjustedStart, colSpan: colSpan, topLevel: targetTrackIndex });
                });

                const isWeekRowExpanded = week.some(dayCell => expandedDates.includes(dayjs(dayCell.date || dayCell).format('YYYY-MM-DD')));
                const totalTracksCount = timelineTracks.length;
                const currentVisibleTracksCount = isWeekRowExpanded ? totalTracksCount : Math.min(maxVisibleCount, totalTracksCount);
                const containerHeight = 40 + (currentVisibleTracksCount * (typeof window !== 'undefined' && window.innerWidth <= 480 ? 24 : 28)) + (totalTracksCount > maxVisibleCount ? (typeof window !== 'undefined' && window.innerWidth <= 480 ? 4 : 24) : 10);
                                return (
                    <Box 
                        key={weekIdx} 
                        sx={{ 
                            position: 'relative', 
                            backgroundColor: '#ffffff', 
                            display: 'grid', 
                            gridTemplateColumns: `repeat(${weekendsVisible ? 7 : 5}, 1fr)`, 
                            gap: '1px', 
                            minHeight: isCalFull ? 'auto' : `${Math.max(135, containerHeight)}px`, 
                            overflow: isCalFull ? 'visible' : 'hidden' 
                        }}
                    >
                        {week.map((dayCell, dayIdx) => {
                            const dayDate = dayjs(dayCell.date || dayCell);
                            const dayOfWeek = dayDate.day();
                            if (!weekendsVisible && (dayOfWeek === 0 || dayOfWeek === 6)) return null;

                            const isCurrentMonth = dayDate.month() === activeCurrentMonth;
                            const isToday = dayjs().isSame(dayDate, 'day');
                            const formattedDate = dayDate.format('YYYY-MM-DD');

                            // 📅 [공휴일 데이터 매핑]
                            const currentHoliday = (holidays || []).find(h => h.holidayDate === formattedDate);
                            const isHoliday = !!currentHoliday;
                            const holidayName = currentHoliday ? currentHoliday.comment : '';
                            
                            // 💡 공휴일 타입이 반일(HALF)인지 확인
                            const isHalfHoliday = currentHoliday && currentHoliday.workType === 'HALF';

                            const dayAllEvents = renderedWeekEvents.filter(event => {
                                const eStartStr = event.start.substring(0, 10);
                                const eEndStr = event.end.substring(0, 10);
                                return formattedDate >= eStartStr && formattedDate <= eEndStr;
                            });

                            const hasHiddenTracks = dayAllEvents.some(e => e.topLevel >= maxVisibleCount);
                            const isExpanded = expandedDates.includes(formattedDate);
                            const maxTrackInWeek = week.reduce((max, dayCell) => {
                                const dDate = dayjs(dayCell.date || dayCell).format('YYYY-MM-DD');
                                const dEvents = renderedWeekEvents.filter(e => {
                                    const s = e.start.substring(0, 10);
                                    const ed = e.end.substring(0, 10);
                                    return dDate >= s && dDate <= ed;
                                });
                                const maxTopLevel = dEvents.length > 0 ? Math.max(...dEvents.map(e => e.topLevel || 0)) : 0;
                                return maxTopLevel > max ? maxTopLevel : max;
                            }, 0);

                            const dynamicFullHeight = 40 + ((maxTrackInWeek + 1) * 28) + 16;
                            const finalMinHeight = Math.max(120, dynamicFullHeight);
                            const isSalesPath = window.location.pathname === '/salesActivity';

                            const shouldExpandAll = isCalFull || isSalesPath;

                            return (
                                <Box 
                                    key={dayIdx} 
                                    onClick={() => handleDateClick && handleDateClick(formattedDate)} 
                                    sx={{ 
                                        backgroundColor: isCurrentMonth ? (isToday ? 'rgba(25, 118, 210, 0.04)' : '#ffffff') : '#fafafa', 
                                        p: 1, 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        boxSizing: 'border-box', 
                                        borderRight: dayIdx < 6 ? '1px solid #e0e0e0' : 'none', 
                                        position: 'relative', 
                                        zIndex: 1, 
                                        cursor: 'pointer',
                                        height: '100%', 
                                        minHeight: shouldExpandAll ? `${finalMinHeight}px` : '120px', 
                                        overflow: shouldExpandAll ? 'visible' : 'hidden',
                                        pb: shouldExpandAll ? '24px' : '8px' 
                                    }}
                                >
                                    {/* 🏷️ 날짜 숫자와 휴일 텍스트 가로 배치를 위한 레이아웃 상자 */}
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', mb: 1, flexWrap: 'wrap' }}>
                                        <Typography 
                                            variant="body1" 
                                            fontWeight="800" 
                                            sx={{ 
                                                mb: 1, 
                                                opacity: isCurrentMonth ? 1 : 0.4, 
                                                // 🎨 [날짜 색상 조율]: 
                                                // 일요일(0)이거나 '반차가 아닌' 일반 공휴일만 빨간색(#dc3545)으로 표시합니다.
                                                // 반차(isHalfHoliday)인 날은 평일이므로 토요일(6)이 아니라면 무조건 일반 검정색(#333333)으로 표현됩니다.
                                                color: (dayOfWeek === 0 || (isHoliday && !isHalfHoliday)) 
                                                    ? '#dc3545' 
                                                    : dayOfWeek === 6 
                                                        ? '#0d6efd' 
                                                        : '#333333', 
                                                fontSize: '14px' 
                                            }}
                                        >
                                            {dayDate.date()}
                                        </Typography>

                                        {/* 📌 휴일 명칭 표시 */}
                                        {isHoliday && holidayName && (
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    // 🎨 [요구사항 핵심]: 일반 휴일 문구는 빨간색, 반차(HALF)인 날의 문구만 선명한 파란색(#0d6efd) 처리
                                                    color: isHalfHoliday ? '#0d6efd' : '#dc3545', 
                                                    fontWeight: 'bold', 
                                                    fontSize: '11px', 
                                                    lineHeight: 1,
                                                    opacity: isCurrentMonth ? 0.9 : 0.4,
                                                    '@media (max-width: 480px)': { fontSize: '9px' } 
                                                }}
                                            >
                                                {holidayName}
                                            </Typography>
                                        )}
                                    </Box>

                                    {hasHiddenTracks && !shouldExpandAll && (
                                        <Typography 
                                            onClick={(e) => toggleExpand(formattedDate, e)} 
                                            variant="caption" 
                                            sx={{ 
                                                position: 'absolute', 
                                                bottom: '6px', 
                                                right: '8px', 
                                                color: isExpanded ? '#dc3545' : '#0052cc', 
                                                fontWeight: 'bold', 
                                                cursor: 'pointer', 
                                                zIndex: 5, 
                                                fontSize: '12px !important',
                                                backgroundColor: isCurrentMonth ? (isToday ? 'rgba(25, 118, 210, 0.04)' : '#ffffff') : '#fafafa', 
                                                pl: '4px', 
                                                '&:hover': { textDecoration: 'underline' },
                                                '@media (max-width: 480px)': {
                                                    fontSize: '8px !important',
                                                    bottom: '3px !important',
                                                    right: '4px !important'
                                                }
                                            }}
                                        >
                                            {isExpanded ? '접기 ▲' : `+ 더보기 ▼`}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}

                        <Box 
                            sx={{ 
                                position: 'absolute', 
                                top: '40px', 
                                left: 0, 
                                right: 0, 
                                bottom: 0, 
                                pointerEvents: 'none', 
                                zIndex: 2, 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: '4px',
                                height: (isCalFull || window.location.pathname === '/salesActivity') ? 'auto' : '100%',
                                overflow: (isCalFull || window.location.pathname === '/salesActivity') ? 'visible' : 'hidden',
                                '@media (max-width: 480px)': {
                                    gap: '2px !important'
                                }
                            }}
                        >
                            {timelineTracks.map((_, trackIdx) => {
                                if (trackIdx >= maxVisibleCount && !isWeekRowExpanded && !(isCalFull || window.location.pathname === '/salesActivity')) return null;
                                const currentTrackEvents = renderedWeekEvents.filter(e => e.topLevel === trackIdx);

                                return (
                                    <Box 
                                        key={trackIdx} 
                                        sx={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: `repeat(${weekendsVisible ? 7 : 5}, 1fr)`, 
                                            gap: '1px', 
                                            width: '100%', 
                                            height: '24px',
                                            position: 'relative',
                                            '@media (max-width: 480px)': {
                                                height: '20px !important'
                                            }
                                        }}
                                    >
                                        {currentTrackEvents.map((event) => {
                                            const formatToKorMD = (dateStr) => {
                                                if (!dateStr) return '';
                                                const [_, month, day] = dateStr.substring(0, 10).split('-');
                                                return `${Number(month)}월 ${Number(day)}일`;
                                            };
                                            
                                            const [startDate, startTimeFull] = event.start ? event.start.split(' ') : ['', ''];
                                            const startTime = startTimeFull ? startTimeFull.substring(0, 5) : '';
                                            const startMD = formatToKorMD(startDate);

                                            const [endDate, endTimeFull] = event.end ? event.end.split(' ') : ['', ''];
                                            const endTime = endTimeFull ? endTimeFull.substring(0, 5) : '';
                                            const endMD = formatToKorMD(endDate);

                                            let dateTimeRange = '';
                                            if (startMD) {
                                                if (endTime && startDate !== endDate) {
                                                    dateTimeRange = `(${startMD} ${startTime}~${endMD} ${endTime}) `;
                                                } else if (startTime && endTime) {
                                                    dateTimeRange = `(${startMD} ${startTime}~${endTime}) `;
                                                } else if (startTime) {
                                                    dateTimeRange = `(${startMD} ${startTime}) `;
                                                } else {
                                                    dateTimeRange = `(${startMD}) `;
                                                }
                                            }
                                            
                                            const storageUser = JSON.parse(localStorage.getItem('user'));                                            
                                            const myUuid = storageUser?.userUuid || storageUser?.usrUuid || '';
                                            const eventUsrUuid = event.usrUuid || event.extendedProps?.usrUuid || '';

                                            const managerColors = [
                                                '#2e7d32', '#ed6c02', '#9c27b0', '#00838f', '#6d4c41', 
                                                '#455a64', '#00796b', '#c62828', '#ad1457', '#6a1b9a', 
                                                '#283593', '#1565c0', '#0288d1', '#558b2f', '#f9a825', 
                                                '#ef6c00', '#d84315', '#4e342e', '#37474f', '#546e7a'
                                            ];

                                            let finalBgColor = '#1976d2'; 

                                            if (myUuid !== eventUsrUuid) {
                                                if (eventUsrUuid) {
                                                    let hash = 0;
                                                    for (let i = 0; i < eventUsrUuid.length; i++) {
                                                        hash = eventUsrUuid.charCodeAt(i) + ((hash << 5) - hash);
                                                    }
                                                    const colorIdx = Math.abs(hash) % managerColors.length;
                                                    finalBgColor = managerColors[colorIdx];
                                                } else {
                                                    finalBgColor = '#2e7d32';
                                                }
                                            }

                                            const isLocationValid = window.location.pathname === '/salesActivity';
                                            const eventUsrNm = event.usrNm || event.extendedProps?.usrNm || '';
                                            const prefixName = (!isLocationValid && eventUsrNm) ? `[${eventUsrNm}] ` : '';
                                            return (
                                                <Box 
                                                    key={event.id} 
                                                    onClick={(e) => handleEventClick && handleEventClick(event, e)} 
                                                    sx={{ 
                                                        gridColumnStart: event.startIdx + 1, 
                                                        gridColumnEnd: `span ${event.colSpan}`, 
                                                        height: '24px', 
                                                        backgroundColor: finalBgColor, 
                                                        color: '#ffffff', 
                                                        px: '8px', 
                                                        mx: '2px', 
                                                        borderRadius: '4px', 
                                                        fontSize: '12px', 
                                                        lineHeight: '24px', 
                                                        cursor: 'pointer', 
                                                        whiteSpace: 'nowrap', 
                                                        overflow: 'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        pointerEvents: 'auto', 
                                                        zIndex: 2, 
                                                        boxSizing: 'border-box', 
                                                        boxShadow: '0 1px 2px rgba(0,0,0,0.12)', 
                                                        opacity: event.end && dayjs(event.end).isBefore(dayjs(), 'day') ? 0.45 : 1,
                                                        '@media (max-width: 480px)': {
                                                            fontSize: '8px !important',
                                                            px: '2px !important',
                                                            mx: '1px !important',
                                                            height: '20px !important',
                                                            lineHeight: '20px !important'
                                                        },
                                                        '&:hover': { 
                                                            zIndex: 10, 
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                                                            opacity: 1
                                                        } 
                                                    }} 
                                                    title={`${prefixName}${event.title} ${dateTimeRange}`}
                                                >
                                                    {prefixName}{event.title} {dateTimeRange}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default CalendarGrid;
